import React from "react";
import { api, extractApiError } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { pickLocalized } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Clock } from "lucide-react";
// Relative on purpose: no "@" alias under Jest (cf. components/ui/FieldError.jsx).
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { limitToText, textToLimit } from "../limitText";

/**
 * One practice's entitlements, edited by a super admin.
 *
 * What a tenant can use is its SNAPSHOT (`tenants.entitlements`), taken when it bought or
 * changed plan — not the plan's current matrix (grandfathering, contract "Owner
 * decisions" #2). This editor is the one way to change a single practice without
 * changing its plan: `PUT /super-admin/tenants/{id}/entitlements` REPLACES the snapshot
 * with `{capabilities: [key], limits: {<limit_field>: int}}` (source "super_admin").
 *
 * Reads two things:
 *   GET /super-admin/tenants/{id}/entitlements → {plan_id, capabilities: {key: {granted,
 *       status, eta, source, core, kind, value, used}}}   (the resolved map)
 *   GET /super-admin/capabilities             → {groups, capabilities: [catalogue rows]}
 *       (labels, groups, `core`, `kind`, `limit_field`)
 *
 * What is listed, and why:
 *   - core rows are absent: every tenant has them, and the server refuses a core key;
 *   - live rows are switches;
 *   - coming-soon rows are shown, disabled: they cannot be granted a working surface yet,
 *     but a snapshot that already carries one (a plan promised it) keeps it — the PUT
 *     re-sends it, so opening and saving this editor never silently takes a promise away;
 *   - a module capability owned as a separately paid add-on (`source: "addon"`) is shown
 *     as such, disabled: it is not part of the snapshot, and unticking it here could not
 *     remove it;
 *   - the LIVE limit rows are numbers (blank = unlimited = -1), with the current usage
 *     beside; a coming-soon limit row is not listed and never sent.
 */

/**
 * The keys this practice's snapshot holds — what a save must re-send unless the operator
 * untoggles one.
 *
 * Read from the RAW snapshot (`entitlements.capabilities`, which the super-admin GET
 * returns beside the resolved map) because the resolved map cannot say it: a coming-soon
 * key the practice bought resolves to `granted: false` (status before grant), and an
 * INACTIVE catalogue key is absent from it altogether. Deriving membership from `granted`
 * made every open-and-save silently drop both. A practice with no raw snapshot (should
 * not exist after the backfill) falls back to the resolved `source`, whatever `granted`
 * says. Add-on grants (`source: "addon"`) are never snapshot members.
 */
function snapshotKeys(view) {
  const raw = view?.entitlements?.capabilities;
  if (Array.isArray(raw)) return new Set(raw.filter((k) => typeof k === "string"));
  const resolved = view?.capabilities || {};
  return new Set(Object.keys(resolved).filter((k) => {
    const src = resolved[k]?.source;
    return (src === "plan" || src === "super_admin") && !resolved[k]?.core;
  }));
}

/** 409 PAID_LINE_DEPENDS `lines`: add-on keys, or `{key}` objects. */
const lineKeys = (lines) => (Array.isArray(lines) ? lines : [])
  .map((l) => (typeof l === "string" ? l : l?.key))
  .filter(Boolean);


function localizedLabel(row, lang) {
  return pickLocalized(
    { label_en: row.label, label_fr: row.label_i18n?.fr, label_es: row.label_i18n?.es },
    "label",
    lang,
  );
}

export const EntitlementsEditorModal = ({ tenant, onClose, onSaved, t }) => {
  const { lang } = useLanguage();
  // Add-on NAMES are the practitioner's billing copy (`bill.addonName.<key>`, admin
  // namespace); one wording for the same line on both sides of the product.
  const ta = useT("admin");
  const [phase, setPhase] = React.useState("loading"); // loading | ready | error
  const [loadError, setLoadError] = React.useState(null);
  const [rows, setRows] = React.useState([]); // toggleable/visible boolean rows
  const [limitRows, setLimitRows] = React.useState([]);
  const [selected, setSelected] = React.useState(new Set());
  const [limits, setLimits] = React.useState({});
  const [limitErrors, setLimitErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const tenantId = tenant?.tenant_id;

  const load = React.useCallback(async () => {
    if (!tenantId) return;
    setPhase("loading");
    setLoadError(null);
    try {
      const [entRes, catRes] = await Promise.all([
        api.get(`/super-admin/tenants/${tenantId}/entitlements`),
        api.get("/super-admin/capabilities"),
      ]);
      const resolved = entRes.data?.capabilities || {};
      const groups = catRes.data?.groups || [];
      const catalogue = (catRes.data?.capabilities || []).filter((c) => c.active !== false && !c.core);
      const groupOrder = Object.fromEntries(groups.map((g) => [g.key, g.order ?? 0]));
      const byOrder = (a, b) =>
        (groupOrder[a.group] ?? 0) - (groupOrder[b.group] ?? 0) || (a.order ?? 0) - (b.order ?? 0);

      const booleans = catalogue.filter((c) => c.kind !== "limit").sort(byOrder).map((c) => ({
        key: c.key,
        label: localizedLabel(c, lang),
        comingSoon: c.status !== "live",
        eta: c.eta || null,
        addon: resolved[c.key]?.source === "addon",
      }));
      // Only LIVE limit rows are edited and sent. A coming-soon limit (e.g.
      // team.practitioners: no team accounts exist) has nothing to limit yet, and the
      // entitlements PUT refuses a limit field it does not accept (422).
      const limitsCat = catalogue.filter((c) => c.kind === "limit" && c.limit_field && c.status === "live").sort(byOrder).map((c) => ({
        key: c.key,
        field: c.limit_field,
        label: localizedLabel(c, lang),
        used: Number.isInteger(resolved[c.key]?.used) ? resolved[c.key].used : null,
      }));

      setRows(booleans);
      setLimitRows(limitsCat);
      // EVERY snapshot key, listed or not: the rows below toggle the ones on screen, and
      // the rest (inactive keys, limit-row keys) travel back unchanged.
      setSelected(snapshotKeys(entRes.data));
      setLimits(Object.fromEntries(limitsCat.map((r) => [r.field, limitToText(resolved[r.key]?.value)])));
      setLimitErrors({});
      setPhase("ready");
    } catch (e) {
      setLoadError(e);
      setPhase("error");
    }
  }, [tenantId, lang]);

  React.useEffect(() => { load(); }, [load]);

  if (!tenant) return null;

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    const parsed = {};
    const errors = {};
    limitRows.forEach((r) => {
      const v = textToLimit(limits[r.field]);
      if (v === null) errors[r.field] = t("tent.limitInvalid");
      else parsed[r.field] = v;
    });
    if (Object.keys(errors).length) { setLimitErrors(errors); return; }
    setSaving(true);
    try {
      await api.put(`/super-admin/tenants/${tenantId}/entitlements`, {
        capabilities: Array.from(selected),
        limits: parsed,
      });
      toast.success(t("tent.saved", { name: tenant.name || tenant.slug }));
      onSaved();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "PAID_LINE_DEPENDS") {
        const names = lineKeys(detail.lines).map((k) => ta(`bill.addonName.${k}`, null, k));
        toast.error(t("tent.paidLineDepends", { lines: names.join(", ") }));
      } else if (e?.response?.status === 422 && detail?.code === "UNKNOWN_CAPABILITY") {
        toast.error(t("pfm.unknownCapabilityError", { keys: (detail.keys || []).join(", ") }));
      } else {
        toast.error(extractApiError(e, t("tl.error"), t));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="entitlements-editor"
        className="bg-white border border-slate-200 rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <h3 className="font-sol font-semibold text-2xl mb-1">{t("tent.title", { name: tenant.name || tenant.slug })}</h3>
        <p className="text-slate-400 text-sm mb-2">/t/{tenant.slug}</p>
        <p className="text-slate-500 text-xs mb-6" data-testid="entitlements-editor-note">{t("tent.note")}</p>

        {phase === "loading" && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}

        {phase === "error" && (
          <div data-testid="entitlements-editor-load-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
            {extractApiError(loadError, t("tent.loadError"), t)}
            <button type="button" onClick={load} className="ml-2 underline" data-testid="entitlements-editor-retry">
              {t("tent.retry")}
            </button>
          </div>
        )}

        {phase === "ready" && (
          <>
            <div className="space-y-2">
              {rows.map((r) => {
                const disabled = r.comingSoon || r.addon;
                const checked = r.addon || selected.has(r.key);
                return (
                  <label
                    key={r.key}
                    data-testid={`entitlement-row-${r.key}`}
                    className={`flex items-center justify-between gap-4 p-3 rounded-2xl border transition ${
                      disabled ? "bg-slate-50 border-slate-200 cursor-not-allowed" : "bg-white border-slate-200 cursor-pointer hover:border-sol-gold/40"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-slate-800">{r.label}</div>
                      <div className="text-[11px] text-slate-400">{r.key}</div>
                      {r.comingSoon && (
                        <span data-testid={`entitlement-soon-${r.key}`} className="inline-flex items-center gap-1 mt-1 text-[11px] text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">
                          <Clock className="w-3 h-3" /> {t("pfm.comingSoonBadge")}{r.eta ? ` · ${r.eta}` : ""}
                        </span>
                      )}
                      {r.addon && (
                        <span data-testid={`entitlement-addon-${r.key}`} className="inline-block mt-1 text-[11px] text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
                          {t("tent.paidAddon")}
                        </span>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(r.key)}
                      data-testid={`entitlement-toggle-${r.key}`}
                      className="w-5 h-5 shrink-0"
                    />
                  </label>
                );
              })}
            </div>

            {limitRows.length > 0 && (
              <div className="mt-6 space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-400">{t("tent.limits")}</div>
                {limitRows.map((r) => (
                  <div key={r.field} className="flex items-start justify-between gap-4">
                    <div className="min-w-0 pt-2">
                      <div className="text-sm text-slate-800">{r.label}</div>
                      {r.used !== null && (
                        <div className="text-[11px] text-slate-400" data-testid={`entitlement-used-${r.field}`}>
                          {t("tent.used", { n: r.used })}
                        </div>
                      )}
                    </div>
                    <div className="w-32">
                      <input
                        inputMode="numeric"
                        value={limits[r.field] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLimits((prev) => ({ ...prev, [r.field]: v }));
                          setLimitErrors((prev) => ({ ...prev, [r.field]: "" }));
                        }}
                        placeholder={t("pfm.limitPlaceholder")}
                        aria-label={r.label}
                        aria-invalid={limitErrors[r.field] ? true : undefined}
                        data-testid={`entitlement-limit-${r.field}`}
                        className={`w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-center ${limitErrors[r.field] ? invalidInputClass : ""}`}
                      />
                      <FieldError message={limitErrors[r.field] || ""} testId={`entitlement-limit-${r.field}-error`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex gap-2 mt-6">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 rounded-full border-slate-200"
            data-testid="entitlements-editor-close"
          >
            {t("tl.cancel")}
          </Button>
          <Button
            onClick={save}
            disabled={phase !== "ready" || saving}
            className="flex-1 bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full"
            data-testid="entitlements-editor-save"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("tent.save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EntitlementsEditorModal;
